import type { Lesson } from './courses';

/**
 * Parcours 4 : Sales Intelligence — 6 lecons.
 */
export const SALES_INTELLIGENCE_LESSONS: Lesson[] = [
  {
    slug: 'sales-basics',
    title: 'Sales Intelligence : comprendre le marche',
    duration: '4 min',
    glossaryTerms: ['sales-intelligence', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La Sales Intelligence (intelligence commerciale) designe l'ensemble des donnees et outils qui aident les equipes commerciales a mieux comprendre leur marche, leurs prospects et leurs concurrents. C'est l'art de vendre en etant mieux informe que la concurrence.\n\nElle couvre 3 axes :\n- **Connaissance marche** — Tendances, taille du marche, segments porteurs\n- **Connaissance client** — Besoins, douleurs, processus d'achat des prospects\n- **Connaissance concurrence** — Forces, faiblesses, positionnement des concurrents",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les commerciaux qui utilisent des outils de Sales Intelligence ont un taux de conversion 35% superieur a ceux qui n'en utilisent pas (Forrester). Dans un marche B2B, ou le cycle de vente est long et les decisions se prennent a plusieurs, etre bien informe fait la difference entre signer un contrat et perdre face a un concurrent.\n\nEn moyenne, un commercial passe 21% de son temps a rechercher des informations. La Sales Intelligence automatise cette recherche.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez votre ICP** (Ideal Customer Profile) — Secteur, taille, budget, douleurs principales\n2. **Analysez vos concurrents** — Identifiez 3-5 concurrents principaux, etudiez leur offre et prix\n3. **Creez des battle cards** — Fiches comparatives pour chaque concurrent\n4. **Profilez vos prospects** — Avant chaque contact, rassemblez les informations cles\n5. **Mesurez et ajustez** — Suivez vos taux de conversion par segment",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Sales Intelligence** centralise vos outils commerciaux : generation de battle cards par l'IA, profiling de prospects, et creation d'emails de prospection personnalises. Tout est alimente par votre Knowledge Base pour un maximum de pertinence.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'battle-cards',
    title: 'Les Battle Cards (fiches argumentaires)',
    duration: '4 min',
    glossaryTerms: ['battle-cards', 'sales-intelligence'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Les battle cards (fiches argumentaires) sont des documents synthetiques qui positionnent votre produit face a un concurrent specifique. Elles fournissent a vos commerciaux les arguments cles pour repondre aux objections et mettre en valeur vos differenciateurs.\n\nUne battle card typique contient :\n- **Vue d'ensemble du concurrent** — Ce qu'il fait, son positionnement\n- **Forces et faiblesses** — Ce qu'il fait bien, ce qu'il fait moins bien\n- **Vos differenciateurs** — Ce que vous faites mieux ou differemment\n- **Reponses aux objections** — Arguments prepares pour les questions frequentes",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "71% des acheteurs B2B comparent au moins 3 solutions avant de decider (Gartner). Vos prospects vont inevitablement vous comparer a vos concurrents. Si vos commerciaux ne sont pas prepares, ils perdront ces deals.\n\nLes equipes qui utilisent des battle cards remportent 30% de deals en plus dans les situations concurrentielles.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez vos 3-5 concurrents principaux** — Ceux que vos prospects mentionnent le plus souvent\n2. **Collectez les informations** — Site web, tarifs, avis clients, fonctionnalites\n3. **Structurez la fiche** — Resume, forces/faiblesses, differenciateurs, objections\n4. **Gardez-les a jour** — Revoyez chaque trimestre, les concurrents evoluent\n5. **Formez vos equipes** — Chaque commercial doit maitriser les battle cards",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Sales Intelligence** genere des battle cards automatiquement grace a l'IA. Renseignez le nom du concurrent et l'IA analyse les informations disponibles pour produire une fiche argumentaire structuree, prete a l'emploi.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'cold-outreach',
    title: 'Cold Outreach : prospection a froid',
    duration: '4 min',
    glossaryTerms: ['cold-outreach', 'prospect-pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le cold outreach (prospection a froid) consiste a contacter des prospects qui ne vous connaissent pas encore. C'est un canal d'acquisition puissant en B2B quand il est bien execute.\n\nLa cle du succes : la personnalisation. Un email generique aura un taux de reponse de 1-2%. Un email personnalise, qui montre que vous avez fait vos recherches, atteint 5-15%.\n\nLe cold outreach n'est PAS du spam. C'est une approche ciblee, personnalisee et respectueuse.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le cold outreach reste le canal B2B avec le meilleur cout par lead qualifie. Un email bien personnalise ne coute pratiquement rien a envoyer, et peut generer des opportunites a 5 ou 6 chiffres.\n\n82% des acheteurs acceptent des rendez-vous avec des vendeurs qui les contactent en premier (RAIN Group). Mais attention : la qualite prime sur la quantite. 10 emails ultra-personnalises valent mieux que 1 000 emails generiques.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Recherchez le prospect** — LinkedIn, site web, actualites recentes\n2. **Trouvez un angle d'accroche** — Un post recent, un changement de poste, une actualite entreprise\n3. **Redigez une premiere ligne personnalisee** — Montrez que vous avez fait vos devoirs\n4. **Proposez de la valeur** — Un insight, une ressource, pas un pitch produit\n5. **Un seul CTA clair** — \"15 minutes pour en discuter ?\" suffit\n6. **Relancez intelligemment** — 3 relances max, espacees de 3-5 jours",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** genere des emails de cold outreach personnalises. Renseignez le nom du prospect et son contexte, et l'IA produit un email adapte. Combinez avec le **Prospect Profiler** dans Sales Intelligence pour enrichir vos recherches avant le contact.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'prospect-pipeline',
    title: 'Gerer son pipeline de prospects',
    duration: '3 min',
    glossaryTerms: ['prospect-pipeline', 'pipeline', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le pipeline de prospects (ou tunnel de prospection) represente les differentes etapes par lesquelles passe un prospect avant de devenir client. Chaque etape correspond a un niveau de maturite :\n\n- **Nouveau** — Prospect identifie, pas encore contacte\n- **Contacte** — Premier email ou message envoye\n- **Relance** — Suivi apres le premier contact\n- **En negociation** — Discussion active sur l'offre\n- **Converti** — Le prospect est devenu client\n- **Perdu** — Le prospect a decline l'offre",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans pipeline structure, les prospects tombent dans l'oubli. Vous oubliez de relancer, vous perdez la trace des conversations, et des opportunites glissent entre les mailles.\n\nLes equipes qui utilisent un pipeline structurent mieux leurs efforts : elles savent combien de prospects sont a chaque etape, ou se situent les blocages, et quelles actions prioriser. Resultat : des taux de conversion 28% plus eleves (Salesforce).",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez vos etapes** — Adaptez le pipeline a votre cycle de vente\n2. **Fixez des criteres de passage** — Quand un prospect passe-t-il de \"contacte\" a \"en negociation\" ?\n3. **Suivez les metriques** — Taux de conversion entre chaque etape\n4. **Priorisez les actions** — Concentrez-vous sur les prospects les plus avances\n5. **Nettoyez regulierement** — Supprimez les prospects inactifs depuis plus de 30 jours",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Sales Intelligence** inclut un pipeline visuel pour suivre vos prospects a travers chaque etape. Glissez-deposez les prospects entre les colonnes, ajoutez des notes et planifiez vos relances.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'prospect-profiling',
    title: "Profiler un prospect avec l'IA",
    duration: '3 min',
    glossaryTerms: ['sales-intelligence', 'prospect-pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le profiling de prospects consiste a rassembler et structurer les informations cles sur un prospect avant de le contacter. L'objectif : comprendre ses besoins, ses douleurs et son contexte pour personnaliser votre approche.\n\nUn bon profil prospect inclut :\n- **Informations entreprise** — Secteur, taille, chiffre d'affaires, actualites\n- **Informations contact** — Poste, responsabilites, historique professionnel\n- **Besoins potentiels** — Douleurs probables liees a votre offre\n- **Points d'accroche** — Contenus publies, prises de parole, centres d'interet",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un commercial qui contacte un prospect sans le connaitre envoie un signal negatif : \"je n'ai pas fait l'effort de m'interesser a vous\". A l'inverse, une approche informee cree immediatement de la confiance.\n\nLes emails personnalises avec des references au contexte du prospect ont un taux de reponse 3 a 5 fois superieur aux emails generiques. L'IA permet d'automatiser cette recherche pour chaque prospect.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Renseignez le nom ou l'URL LinkedIn du prospect** — C'est le point de depart\n2. **Laissez l'IA analyser** — Elle collecte et structure les informations publiques\n3. **Identifiez les douleurs** — L'IA suggere des besoins potentiels lies a votre offre\n4. **Preparez votre pitch** — Utilisez les points d'accroche identifies\n5. **Personnalisez votre email** — Integrez les insights dans votre cold outreach",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Prospect Profiler** dans Sales Intelligence genere automatiquement un profil structure a partir du nom ou de l'URL LinkedIn d'un prospect. L'IA identifie les besoins potentiels et suggere des angles d'approche personnalises.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'your-sales-tools',
    title: 'Sales Intelligence dans FO',
    duration: '3 min',
    glossaryTerms: ['sales-intelligence', 'battle-cards', 'cold-outreach', 'prospect-pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le module Sales Intelligence de Feedback Orchestrator regroupe tous vos outils commerciaux en un seul endroit : battle cards, profiling de prospects, generation d'emails de prospection et pipeline de suivi. Le tout alimente par l'IA et votre Knowledge Base.",
      },
      {
        type: 'methodology',
        title: "Comment l'utiliser ?",
        content:
          "1. **Generez vos battle cards** — Renseignez vos concurrents et l'IA cree les fiches argumentaires\n2. **Profilez vos prospects** — Utilisez le Prospect Profiler avant chaque prise de contact\n3. **Creez vos emails** — La Content Factory genere des cold outreach personnalises\n4. **Suivez votre pipeline** — Deplacez les prospects entre les etapes au fil des interactions\n5. **Analysez vos resultats** — Consultez le Marketing Dashboard pour voir vos taux de conversion",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur **Sales Intelligence** pour acceder a vos outils commerciaux. Les battle cards sont accessibles en un clic, le prospect profiler genere des fiches en quelques secondes, et le pipeline visuel vous donne une vue d'ensemble de votre activite commerciale.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
];
